import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { planPath, useResetOnBackForward, type PlanMode } from "@/components/calendar/calendar-flow";
import { showPillToast } from "@/components/calendar/pill-toast";
import { Badge, DarkPill, OutlinePill, StepContextCard, calendarDialogClass, calendarOverlayClass } from "@/components/calendar/ui";
import { PlanCard, PlanFooter, PlanNotice, PlanTopBar, SegmentedControl, StepPanel } from "@/components/calendar/plan/plan-parts";
import { DayPicker, MobileDayStrip } from "@/components/calendar/plan/day-picker";
import { SlotList, slotKey } from "@/components/calendar/plan/slot-list";
import { DayTimeline, FreeBusyEmptyState, FromGoogleBadge, TimelineLegend } from "@/components/calendar/plan/day-timeline";
import { RhythmBuilder, RhythmReviewDialog, WeekView } from "@/components/calendar/plan/rhythm";
import { useMomentum } from "@/hooks/use-momentum";
import {
  CALENDAR_CONNECTION_KEY,
  useBusy,
  useCalendarConnection,
  useCalendarHistory,
  useCreateSchedule,
  useMoveEvent,
  useReplaceRhythm,
  useStartGoogleConnect,
  useStepSchedules,
  useUpdateConnection,
} from "@/hooks/use-calendar";
import { calendarErrorCode } from "@/lib/calendar-api";
import {
  clearCalendarResult,
  clearHandoff,
  readCalendarResult,
  readHandoff,
  returnUrl,
  saveHandoff,
  type PlanDraft,
} from "@/lib/calendar-handoff";
import {
  DURATIONS,
  addDays,
  atMinutes,
  dayKey,
  formatDay,
  formatMonthDay,
  formatTime,
  formatTimeRange,
  minutesOf,
  nextEvent,
  parseDayKey,
  rhythmDaysLabel,
  rhythmLabel,
  sameDay,
  startOfDay,
  timeZoneLabel,
  weekdayLong,
  type CalendarConnection,
  type Frequency,
  type Rhythm,
  type StepSchedule,
  type TimeOfDay,
} from "@/lib/calendar";
import {
  checkOwnTime,
  checkRhythm,
  commonSlots,
  datesToAdd,
  firstStart,
  freeDots,
  busyOnDay,
  overlapping,
  rhythmTally,
  suggestRhythms,
  suggestSlots,
  weekdayFit,
  type Interval,
} from "@/lib/calendar-slots";
import { monthKey, type MomentumItem } from "@/lib/momentum";

const MOBILE_QUERY = "(max-width: 767px)";
const PART_END: Record<TimeOfDay, number> = { morning: 12 * 60, afternoon: 17 * 60, evening: 21 * 60, any: 20 * 60 };
const PREFER_HINT: Record<TimeOfDay, string> = {
  morning: "mornings first",
  afternoon: "afternoons first",
  evening: "evenings first",
  any: "best fit first",
};
/** Shown while busy times load, so the timeline has shape before it has data. */
const LOADING_BLOCKS: Interval[] = [
  { start: 600, end: 690 },
  { start: 720, end: 750 },
  { start: 780, end: 810 },
  { start: 1140, end: 1200 },
];

function readMode(): PlanMode {
  const m = new URLSearchParams(window.location.search).get("mode");
  return m === "again" || m === "reschedule" || m === "rhythm" ? m : "new";
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const q = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(q.matches);
    q.addEventListener("change", onChange);
    return () => q.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

function momentumHref(item: MomentumItem): string {
  const d = new Date(item.journalCreatedAt);
  return Number.isNaN(d.getTime()) ? "/momentum" : `/momentum?month=${monthKey(d.getFullYear(), d.getMonth())}`;
}

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f6f2]">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

/** Find a time (J05–J14, R02–R04): from a Next Step to a time that fits, once or as a rhythm. */
export default function PlanStepScreen() {
  const { journalId, actionId } = useParams<{ journalId: string; actionId: string }>();
  const { data: items, isLoading: itemsLoading } = useMomentum();
  const { connection, isLoading: connectionLoading, available } = useCalendarConnection();
  const { schedules, isLoading: schedulesLoading } = useStepSchedules(available);

  if (itemsLoading || connectionLoading || (available && schedulesLoading)) return <Spinner />;
  if (!available) return <Redirect to="/momentum" />;
  const item = items?.find((i) => i.id === actionId && i.journalId === journalId);
  if (!item) return <StepGone />;
  if (!connection) return <ConnectFirst item={item} mode={readMode()} />;
  return <Planner item={item} connection={connection} schedule={schedules.get(item.id)} mode={readMode()} />;
}

function StepGone() {
  const [, navigate] = useLocation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f6f2] px-5">
      <PlanCard className="max-w-md p-8 text-center">
        <p className="font-display text-xl font-semibold text-foreground">This step isn&apos;t here anymore</p>
        <p className="mt-2 text-sm text-muted-foreground">It may have been edited or removed from its Unfolding.</p>
        <DarkPill className="mt-6" onClick={() => navigate("/momentum")}>
          Back to Momentum
        </DarkPill>
      </PlanCard>
    </div>
  );
}

/** Landing here without a connection (a disconnect elsewhere, or an old link). */
function ConnectFirst({ item, mode }: { item: MomentumItem; mode: PlanMode }) {
  const [, navigate] = useLocation();
  const startConnect = useStartGoogleConnect();
  useResetOnBackForward(startConnect);
  const [result] = useState(() => readCalendarResult());
  useEffect(() => {
    clearCalendarResult();
    clearHandoff();
  }, []);
  const go = () => {
    const path = planPath(item, mode);
    saveHandoff({ origin: "plan", journalId: item.journalId, actionId: item.id, stepText: item.text, returnPath: path });
    startConnect.mutate({ returnTo: returnUrl(path) });
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f6f2] px-5">
      <PlanCard className="w-full max-w-md p-8">
        <StepContextCard label="Scheduling" text={item.text} />
        <p className="mt-6 font-display text-[22px] font-semibold text-foreground">Connect Google Calendar first</p>
        <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">
          {result === "missing_events"
            ? "UnfoldWIN needs “Add events” to add your step. Nothing was saved — try again and keep that box ticked."
            : "Then you can pick a time that fits your day. Google opens in this tab and brings you back here."}
        </p>
        <div className="mt-6 flex gap-3">
          <OutlinePill onClick={() => navigate(momentumHref(item))}>Back</OutlinePill>
          <DarkPill className="flex-1" loading={startConnect.isPending || startConnect.isSuccess} onClick={go}>
            Connect Google Calendar
          </DarkPill>
        </div>
      </PlanCard>
    </div>
  );
}

function defaultDay(now: Date, prefer: TimeOfDay): Date {
  const today = startOfDay(now);
  return minutesOf(now) + 60 > PART_END[prefer] ? addDays(today, 1) : today;
}

/** Changing a routine that already started: carry on from today for what's left of it. */
function continueRhythm(r: Rhythm, today: Date): Rhythm {
  const start = parseDayKey(r.startDate);
  if (start >= today) return r;
  if (r.weeks === null) return { ...r, startDate: dayKey(today) };
  const end = addDays(start, r.weeks * 7);
  const remaining = Math.max(1, Math.ceil((end.getTime() - today.getTime()) / (7 * 86_400_000)));
  return { ...r, startDate: dayKey(today), weeks: remaining };
}

function mondayOf(d: Date): Date {
  return addDays(startOfDay(d), -((d.getDay() + 6) % 7));
}

function isoRange(day: string, start: number, end: number) {
  const d = parseDayKey(day);
  return { start: atMinutes(d, start).toISOString(), end: atMinutes(d, end).toISOString() };
}

function lowerRhythmDays(r: Pick<Rhythm, "frequency" | "days">): string {
  const label = rhythmDaysLabel(r);
  return label.startsWith("Every") || label === "Weekdays" ? label.charAt(0).toLowerCase() + label.slice(1) : `every ${label}`;
}

function Planner({
  item,
  connection,
  schedule,
  mode,
}: {
  item: MomentumItem;
  connection: CalendarConnection;
  schedule: StepSchedule | undefined;
  mode: PlanMode;
}) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const history = useCalendarHistory();
  const isMobile = useIsMobile();
  // Refreshed every minute and on focus, so a page left open never offers a time that has passed.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const t = window.setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("focus", tick);
    };
  }, []);
  const today = startOfDay(now);
  const existingNext = schedule ? nextEvent(schedule, now) : null;
  const existingRhythm = schedule?.kind === "rhythm" ? schedule.rhythm : null;

  // Back from Google mid-plan (a reconnect, or allowing free/busy): the choice comes back with them.
  const [restored] = useState(() => {
    const handoff = readHandoff();
    const result = readCalendarResult();
    return handoff?.origin === "plan" && handoff.actionId === item.id && result ? { result, draft: handoff.draft } : null;
  });
  useEffect(() => {
    if (!restored) return;
    clearCalendarResult();
    clearHandoff();
    queryClient.invalidateQueries({ queryKey: CALENDAR_CONNECTION_KEY });
    if (restored.result === "connected") {
      showPillToast({
        message: restored.draft ? "Google Calendar reconnected · your choice is still here" : "Google Calendar connected",
        durationMs: 4000,
      });
    }
    if (restored.result === "missing_events") showPillToast({ message: "Google didn't allow adding events · nothing changed", tone: "warning" });
  }, [restored, queryClient]);
  const draft: PlanDraft | undefined = restored?.draft;

  const [prefer, setPrefer] = useState<TimeOfDay>(draft?.prefer ?? history.part ?? "morning");
  const [durationMin, setDuration] = useState<number>(() => {
    if (draft) return draft.durationMin;
    if (existingRhythm) return existingRhythm.durationMin;
    if (mode === "reschedule" && existingNext) {
      const m = (new Date(existingNext.end).getTime() - new Date(existingNext.start).getTime()) / 60000;
      return (DURATIONS as readonly number[]).includes(m) ? m : 30;
    }
    return 30;
  });
  const [frequency, setFrequency] = useState<Frequency>(draft?.frequency ?? (mode === "rhythm" && existingRhythm ? existingRhythm.frequency : "once"));
  const [daySel, setDay] = useState<Date>(() => {
    if (draft?.slot) return parseDayKey(draft.slot.day);
    if (draft) return parseDayKey(draft.day);
    if (existingNext && mode === "reschedule") return startOfDay(new Date(existingNext.start));
    if (existingNext && mode === "again") {
      const after = addDays(startOfDay(new Date(existingNext.start)), 1);
      return after < today ? today : after;
    }
    return defaultDay(now, history.part ?? "morning");
  });
  // A restored or reopened plan never lands on a day that has already gone.
  const day = daySel < today ? today : daySel;
  const [month, setMonth] = useState(() => new Date(day.getFullYear(), day.getMonth(), 1));
  const [picked, setPicked] = useState<string | null>(null);
  const [own, setOwn] = useState(() =>
    draft?.slot && draft.frequency === "once"
      ? { open: true, start: draft.slot.start, selected: true, kept: true }
      : { open: false, start: 9 * 60 + 45, selected: false, kept: false },
  );
  const [calendarId, setCalendarId] = useState(schedule?.calendarId ?? connection.defaultCalendarId);
  const [hideFreeBusyCta, setHideFreeBusyCta] = useState(false);
  const [addError, setAddError] = useState<"connection_ended" | "failed" | null>(null);
  const [daySheetOpen, setDaySheetOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rhythmState, setRhythmState] = useState<Rhythm>(
    () =>
      draft?.rhythm ??
      (existingRhythm ? continueRhythm(existingRhythm, today) : null) ?? {
        frequency: "weekly",
        days: [2, 4],
        minutes: history.typicalMin ?? 7 * 60 + 30,
        durationMin: 30,
        startDate: dayKey(addDays(today, 1)),
        weeks: 8,
        ifBusy: "move",
      },
  );
  // Until the person touches the rhythm, the best suggestion is applied for them.
  const [rhythmTouched, setRhythmTouched] = useState(!!draft?.rhythm || !!existingRhythm);
  // Once the person has picked days or a time themselves, suggestions never overwrite them.
  const [rhythmUserSet, setRhythmUserSet] = useState(!!draft?.rhythm || !!existingRhythm);
  const [personSkips, setPersonSkips] = useState<Set<string>>(() => new Set());
  // The week view follows the rhythm's first date until the person pages through weeks.
  const [weekNav, setWeekNav] = useState<Date | null>(null);

  const isRhythm = frequency !== "once";
  const rhythm: Rhythm = { ...rhythmState, frequency: isRhythm ? frequency : rhythmState.frequency, durationMin };
  const calendarName = connection.calendars.find((c) => c.id === calendarId)?.name ?? connection.account.email;
  const tz = timeZoneLabel(now);

  // ── Busy times ─────────────────────────────────────────────────────────────
  // Free/busy needs Google's permission and the Settings switch. When Google ends the
  // connection mid-plan, times already checked stay on screen (J14) — only new ones can't load.
  const ended = connection.status === "needs_reconnect";
  const freeBusyAllowed = connection.access.freeBusy && connection.suggestFreeTimes;
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  const monthFrom = [monthStart, day].reduce((a, b) => (b < a ? b : a));
  const rangeFrom = monthFrom < today ? today : monthFrom;
  const rangeTo = addDays([monthEnd, addDays(day, 1)].reduce((a, b) => (b > a ? b : a)), 8);
  const monthBusy = useBusy(calendarId, rangeFrom, rangeTo, freeBusyAllowed && !ended);
  const rStart = parseDayKey(rhythm.startDate);
  const rTo = addDays(rStart, (rhythm.weeks ?? 8) * 7 + 1);
  const rhythmBusy = useBusy(calendarId, rStart, rTo, freeBusyAllowed && !ended && isRhythm);
  const busyError = calendarErrorCode(monthBusy.error) ?? calendarErrorCode(rhythmBusy.error);
  // Any other failure (5xx, network) falls back to common times with a retry — never an endless spinner.
  const busyFailed =
    monthBusy.isError && !monthBusy.data && busyError !== "free_busy_not_allowed" && busyError !== "connection_ended";
  const freeBusyOn =
    freeBusyAllowed &&
    busyError !== "free_busy_not_allowed" &&
    !((ended || busyError === "connection_ended") && !monthBusy.data) &&
    !busyFailed;

  useEffect(() => {
    if (busyError === "connection_ended" || busyError === "free_busy_not_allowed") {
      queryClient.invalidateQueries({ queryKey: CALENDAR_CONNECTION_KEY });
    }
  }, [busyError, queryClient]);

  const busyFor = useMemo(() => {
    const cache = new Map<string, Interval[] | null>();
    const ranges = [
      { from: startOfDay(rangeFrom), to: rangeTo, data: monthBusy.data },
      { from: rStart, to: rTo, data: rhythmBusy.data },
    ];
    return (d: Date): Interval[] | null => {
      const key = dayKey(d);
      if (cache.has(key)) return cache.get(key)!;
      const r = ranges.find((x) => x.data && d >= x.from && d < x.to);
      const value = r?.data ? busyOnDay(r.data, d) : null;
      cache.set(key, value);
      return value;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthBusy.data, rhythmBusy.data, dayKey(rangeFrom), dayKey(rangeTo), rhythm.startDate, rhythm.weeks]);
  const noBusy = useCallback(() => null, []);
  const checkedBusyFor = freeBusyOn ? busyFor : noBusy;

  // ── Once ───────────────────────────────────────────────────────────────────
  const dayBusy = freeBusyOn ? busyFor(day) : null;
  const loadingDay = freeBusyOn && !dayBusy;
  const suggestions = useMemo(() => {
    if (!freeBusyOn) return { full: false, slots: commonSlots(day, durationMin, prefer, history, now) };
    if (!dayBusy) return null;
    return suggestSlots({ day, durationMin, busy: dayBusy, prefer, history, now, busyFor });
  }, [freeBusyOn, dayBusy, day, durationMin, prefer, history, now, busyFor]);

  const selectedSlot = own.selected ? null : (suggestions?.slots.find((s) => slotKey(s) === picked) ?? suggestions?.slots[0] ?? null);
  const ownCheck = own.open && dayBusy ? checkOwnTime(own.start, durationMin, dayBusy) : { clash: null, nextFree: null };
  const chosen = own.selected
    ? { day: dayKey(day), start: own.start, end: own.start + durationMin }
    : selectedSlot
      ? { day: selectedSlot.day, start: selectedSlot.start, end: selectedSlot.end }
      : null;
  const chosenBusy = chosen && freeBusyOn ? busyFor(parseDayKey(chosen.day)) : null;
  const chosenOverlaps = !!(chosen && chosenBusy && overlapping({ start: chosen.start, end: chosen.end }, chosenBusy));
  const chosenLabel = chosen ? `${formatDay(parseDayKey(chosen.day))} · ${formatTimeRange(chosen.start, chosen.end)}` : "";

  const dotsFor = useCallback(
    (d: Date) => {
      if (!freeBusyOn) return null;
      const b = busyFor(d);
      return b ? freeDots(b, durationMin, sameDay(d, now) ? firstStart(d, now) : undefined) : null;
    },
    [freeBusyOn, busyFor, durationMin, now],
  );
  /** On today, own times and timeline taps start from the next quarter hour. */
  const minToday = sameDay(day, now) ? firstStart(day, now) : undefined;

  // ── Rhythm ─────────────────────────────────────────────────────────────────
  const rhythmChecking = freeBusyOn && isRhythm && !rhythmBusy.data && !rhythmBusy.isError && !ended;
  const rSuggestions = useMemo(
    () =>
      isRhythm && !rhythmChecking
        ? suggestRhythms({
            frequency: rhythm.frequency,
            durationMin,
            startDate: rhythm.startDate,
            weeks: rhythm.weeks,
            ifBusy: rhythm.ifBusy,
            prefer,
            history,
            busyFor: checkedBusyFor,
            now,
          })
        : [],
    [isRhythm, rhythmChecking, rhythm.frequency, durationMin, rhythm.startDate, rhythm.weeks, rhythm.ifBusy, prefer, history, checkedBusyFor, now],
  );
  useEffect(() => {
    if (!isRhythm || rhythmTouched || !rSuggestions[0]) return;
    setRhythmState((r) => ({ ...r, days: rSuggestions[0].days, minutes: rSuggestions[0].minutes }));
    setRhythmTouched(true);
  }, [isRhythm, rhythmTouched, rSuggestions]);

  const rDates = useMemo(
    () => (isRhythm ? checkRhythm(rhythm, checkedBusyFor, now, personSkips) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRhythm, JSON.stringify(rhythm), checkedBusyFor, now, personSkips],
  );
  const fit = useMemo(
    () => weekdayFit(rhythm, rhythm.minutes, checkedBusyFor, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rhythm.frequency, rhythm.startDate, rhythm.weeks, rhythm.minutes, durationMin, checkedBusyFor, now],
  );
  const toAdd = datesToAdd(rDates);
  const startOptions = Array.from({ length: 14 }, (_, i) => addDays(today, i));
  if (!startOptions.some((d) => dayKey(d) === rhythm.startDate)) startOptions.unshift(parseDayKey(rhythm.startDate));
  const firstShown = toAdd[0] ?? rDates[0];
  const weekStart = weekNav ?? mondayOf(firstShown ? parseDayKey(firstShown.day) : parseDayKey(rhythm.startDate));

  const patchRhythm = (patch: Partial<Rhythm>) => {
    setRhythmTouched(true);
    if (patch.days || patch.minutes !== undefined) setRhythmUserSet(true);
    setPersonSkips(new Set());
    setRhythmState((r) => ({ ...r, ...patch }));
    if (patch.startDate || patch.days) setWeekNav(null);
  };

  const changeFrequency = (f: Frequency) => {
    if (f === frequency) return;
    setFrequency(f);
    if (f === "once") return;
    setPersonSkips(new Set());
    if (!rhythmUserSet) setRhythmTouched(false);
    setRhythmState((r) => ({
      ...r,
      frequency: f,
      days: f === "daily" ? [0, 1, 2, 3, 4, 5, 6] : f === "biweekly" ? r.days.slice(0, 1) : r.days.length > 1 ? r.days : [2, 4],
    }));
  };

  // ── Writes ─────────────────────────────────────────────────────────────────
  const createSchedule = useCreateSchedule();
  const moveEvent = useMoveEvent();
  const replaceRhythm = useReplaceRhythm();
  const updateConnection = useUpdateConnection();
  const startConnect = useStartGoogleConnect();
  useResetOnBackForward(startConnect);
  const pending = createSchedule.isPending || moveEvent.isPending || replaceRhythm.isPending;

  const finish = (message: string, link?: string) => {
    showPillToast({ message, action: link ? { label: "View in Google Calendar", href: link } : undefined });
    if (mode !== "reschedule" && calendarId !== connection.defaultCalendarId) updateConnection.mutate({ defaultCalendarId: calendarId });
    navigate(momentumHref(item));
  };
  const failed = (err: unknown) => {
    setReviewOpen(false);
    setAddError(calendarErrorCode(err) === "connection_ended" ? "connection_ended" : "failed");
  };

  const addOnce = () => {
    if (!chosen) return;
    if (atMinutes(parseDayKey(chosen.day), chosen.start) <= new Date()) {
      setNow(new Date());
      showPillToast({ message: "That time has already passed · pick another one", tone: "warning" });
      return;
    }
    setAddError(null);
    const range = isoRange(chosen.day, chosen.start, chosen.end);
    const when = `${formatDay(parseDayKey(chosen.day))}, ${formatTime(chosen.start)}`;
    if (mode === "reschedule" && existingNext) {
      moveEvent.mutate(
        { actionId: item.id, eventId: existingNext.id, ...range },
        { onSuccess: (s) => finish(`Moved to ${when}`, s?.events.find((e) => e.id === existingNext.id)?.htmlLink), onError: failed },
      );
      return;
    }
    createSchedule.mutate(
      { actionId: item.id, journalId: item.journalId, calendarId, title: item.text, kind: "once", events: [range], rhythm: null },
      {
        onSuccess: (s) => {
          const at = new Date(range.start).getTime();
          const event = s?.events.find((e) => new Date(e.start).getTime() === at) ?? s?.events[s.events.length - 1];
          finish(`${mode === "again" ? "Added again" : "Added to Google Calendar"} · ${when}`, event?.htmlLink);
        },
        onError: failed,
      },
    );
  };

  const addRhythm = () => {
    setAddError(null);
    const events = toAdd.map((d) => isoRange(d.day, d.start, d.start + durationMin));
    const first = toAdd[0];
    if (mode === "rhythm" && schedule) {
      replaceRhythm.mutate(
        { actionId: item.id, calendarId, rhythm, events },
        { onSuccess: (s) => finish(`Rhythm updated · next ${first ? formatDay(parseDayKey(first.day)) : ""}`, s ? nextEvent(s)?.htmlLink : undefined), onError: failed },
      );
      return;
    }
    createSchedule.mutate(
      { actionId: item.id, journalId: item.journalId, calendarId, title: item.text, kind: "rhythm", events, rhythm },
      {
        onSuccess: (s) => finish(`Added to Google Calendar · ${lowerRhythmDays(rhythm)} at ${formatTime(rhythm.minutes)}`, s ? nextEvent(s)?.htmlLink : undefined),
        onError: failed,
      },
    );
  };

  /** Through Google again — to renew access or allow free/busy — keeping the choice for the return. */
  const goToGoogle = () => {
    const path = planPath(item, mode);
    saveHandoff({
      origin: "plan",
      journalId: item.journalId,
      actionId: item.id,
      stepText: item.text,
      returnPath: path,
      draft: { frequency, durationMin, prefer, day: dayKey(day), slot: isRhythm ? null : chosen, rhythm: isRhythm ? rhythm : null },
    });
    startConnect.mutate({ returnTo: returnUrl(path) });
  };
  const allowFreeBusy = () => (connection.access.freeBusy ? updateConnection.mutate({ suggestFreeTimes: true }) : goToGoogle());
  const close = () => navigate(momentumHref(item));

  // ── Copy ───────────────────────────────────────────────────────────────────
  const title = mode === "reschedule" ? "Reschedule a Next Step" : mode === "rhythm" ? "Change the rhythm" : "Plan a Next Step";
  const weekdayName = weekdayLong(day.getDay());
  const listHeading = !freeBusyOn
    ? `Common times on ${formatDay(day)}`
    : loadingDay
      ? `Finding free times on ${formatDay(day)}`
      : suggestions?.full
        ? `${formatDay(day)} is full`
        : `Free times on ${formatDay(day)}`;
  const listHint = !freeBusyOn
    ? "Not checked against your calendar"
    : loadingDay
      ? "Checking your calendar…"
      : suggestions?.full
        ? `No free ${durationMin} minutes`
        : `${durationMin} min · ${PREFER_HINT[prefer]}`;
  const frequencyHint =
    frequency === "weekly"
      ? `${rhythm.days.length} ${rhythm.days.length === 1 ? "time" : "times"} a week`
      : frequency === "daily"
        ? rhythmDaysLabel(rhythm) === "Weekdays"
          ? "Weekdays"
          : rhythm.days.length === 7
            ? "Every day"
            : `${rhythm.days.length} days a week`
        : undefined;

  const notice =
    mode === "again" && existingNext ? (
      <PlanNotice tone="amber" title="Already on your calendar">
        <p>
          {formatDay(new Date(existingNext.start))} · {formatTimeRange(minutesOf(new Date(existingNext.start)), minutesOf(new Date(existingNext.end)))}.
          Adding it again creates a second event.
        </p>
        <a href={existingNext.htmlLink} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-medium text-primary hover:underline">
          View the existing event <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </PlanNotice>
    ) : mode === "reschedule" && existingNext ? (
      <PlanNotice tone="violet" title="Moving your event">
        <p>
          It&apos;s on {formatDay(new Date(existingNext.start))} at {formatTime(minutesOf(new Date(existingNext.start)))} now. Pick a new
          time and it moves in Google Calendar.
        </p>
      </PlanNotice>
    ) : mode === "rhythm" && existingRhythm ? (
      <PlanNotice tone="violet" title="Changing your rhythm">
        <p>{rhythmLabel(existingRhythm)} now. Dates that already happened stay; upcoming ones are replaced.</p>
      </PlanNotice>
    ) : null;

  // ── Footer ─────────────────────────────────────────────────────────────────
  const firstDate = toAdd[0] ? parseDayKey(toAdd[0].day) : null;
  const lastDate = rDates.length ? parseDayKey(rDates[rDates.length - 1].day) : null;
  const rhythmSpan =
    firstDate && lastDate
      ? rhythm.weeks === null
        ? `From ${formatMonthDay(firstDate)} · ongoing`
        : `${formatMonthDay(firstDate)} – ${formatMonthDay(lastDate)} · ${toAdd.length} times`
      : "No dates yet";

  const footer = addError === "connection_ended" ? (
    <PlanFooter
      tone="ended"
      title="Couldn't add this step · your Google Calendar connection ended"
      subtitle={`Nothing was added. Reconnect and your choice stays: ${isRhythm ? rhythmLabel(rhythm) : chosenLabel}.`}
      primaryLabel="Reconnect"
      primaryIcon="reconnect"
      pending={startConnect.isPending || startConnect.isSuccess}
      onPrimary={goToGoogle}
      cancelLabel="Not now"
      onCancel={() => setAddError(null)}
    />
  ) : isRhythm ? (
    <PlanFooter
      tone="rhythm"
      title={rhythmLabel(rhythm)}
      subtitle={`${rhythmSpan} · ${calendarName} · ${tz}`}
      badge={
        freeBusyOn && rDates.length
          ? { tone: rDates.some((d) => d.status === "overlaps") ? "amber" : "green", label: rhythmTally(rDates) }
          : null
      }
      primaryLabel={`Review ${rDates.length} dates`}
      primaryIcon="arrow"
      primaryDisabled={rhythmChecking || rDates.length === 0}
      onPrimary={() => setReviewOpen(true)}
      onCancel={close}
    />
  ) : loadingDay ? (
    <PlanFooter
      tone="loading"
      title="Finding a time that fits…"
      subtitle={`Once · ${calendarName} · ${tz} · your calendar's usual reminders`}
      primaryLabel="Add to calendar"
      primaryDisabled
      onPrimary={() => undefined}
      onCancel={close}
    />
  ) : !chosen ? (
    // Late in the day nothing may be left — say so, rather than looking like it's still loading.
    <PlanFooter
      tone="plain"
      title={`No times left on ${formatDay(day)}`}
      subtitle="Pick another day, or choose your own time."
      primaryLabel="Add to calendar"
      primaryDisabled
      onPrimary={() => undefined}
      onCancel={close}
    />
  ) : (
    <PlanFooter
      tone={chosenOverlaps ? "overlap" : "ok"}
      title={chosenLabel}
      subtitle={`${mode === "again" ? "A second event" : mode === "reschedule" ? "Moves your event" : "Once"} · ${calendarName} · ${tz} · your calendar's usual reminders`}
      badge={
        mode === "again"
          ? { tone: "amber", label: "Second event" }
          : freeBusyOn
            ? chosenOverlaps
              ? { tone: "red", label: "Overlaps a busy time" }
              : { tone: "green", label: "Fits your day" }
            : null
      }
      primaryLabel={mode === "reschedule" ? "Move event" : mode === "again" ? "Add again" : chosenOverlaps ? "Add anyway" : "Add to calendar"}
      pending={pending}
      onPrimary={addOnce}
      onCancel={close}
    />
  );

  // ── Layout ─────────────────────────────────────────────────────────────────
  const timelineStep = chosen && chosen.day === dayKey(day) ? { start: chosen.start, end: chosen.end, overlaps: chosenOverlaps } : null;
  const placeOwn = (m: number) => setOwn({ open: true, start: m, selected: true, kept: false });
  const showFreeBusyCta = !freeBusyOn && !hideFreeBusyCta;

  const dayColumn = (
    <>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold text-foreground">Your day · {formatDay(day)}</h2>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">
            {freeBusyOn ? "Busy times only. Event names and details stay private." : "UnfoldWIN can't see when you're busy yet."}
          </p>
        </div>
        <div className="flex gap-1.5">
          {freeBusyOn ? <FromGoogleBadge /> : <Badge tone="muted">Not connected for availability</Badge>}
          {freeBusyOn && suggestions?.full && <Badge tone="amber">Full day</Badge>}
        </div>
      </div>
      {showFreeBusyCta ? (
        <FreeBusyEmptyState
          reason={busyFailed ? "failed" : ended ? "ended" : connection.access.freeBusy ? "switched_off" : "not_allowed"}
          pending={startConnect.isPending || startConnect.isSuccess || updateConnection.isPending || monthBusy.isFetching}
          onAllow={busyFailed ? () => void monthBusy.refetch() : ended ? goToGoogle : allowFreeBusy}
          onNotNow={() => setHideFreeBusyCta(true)}
        />
      ) : (
        <>
          <DayTimeline
            busy={dayBusy ?? (loadingDay ? LOADING_BLOCKS : [])}
            loading={loadingDay}
            step={timelineStep}
            onPlace={placeOwn}
            minMinutes={minToday}
          />
          <TimelineLegend />
        </>
      )}
    </>
  );

  const slotList = (
    <SlotList
      heading={listHeading}
      hint={listHint}
      loading={loadingDay}
      notice={
        freeBusyOn && suggestions?.full
          ? sameDay(day, now)
            ? `There's no free ${durationMin} minutes left today. These are the nearest times that fit.`
            : `Every gap on ${weekdayName} is shorter than ${durationMin} minutes. These are the nearest times that fit.`
          : undefined
      }
      slots={suggestions?.slots ?? []}
      selectedKey={selectedSlot ? slotKey(selectedSlot) : null}
      pickedDay={dayKey(day)}
      onSelect={(s) => {
        setPicked(slotKey(s));
        setOwn((o) => ({ ...o, selected: false }));
      }}
      own={{ ...own, clash: ownCheck.clash, nextFree: ownCheck.nextFree }}
      durationMin={durationMin}
      onOpenOwn={() =>
        setOwn((o) => ({ ...o, open: true, selected: true, kept: false, start: Math.max(chosen?.start ?? o.start, minToday ?? 0) }))
      }
      onOwnStart={(m) => setOwn((o) => ({ ...o, start: m, kept: false }))}
      onSelectOwn={() => setOwn((o) => ({ ...o, selected: true }))}
      onUseNextFree={() => ownCheck.nextFree !== null && setOwn((o) => ({ ...o, start: ownCheck.nextFree!, kept: false }))}
      onKeepOwn={() => setOwn((o) => ({ ...o, kept: true }))}
      chooseLabel={freeBusyOn && suggestions?.full ? `Choose a time on ${weekdayName} anyway` : "Choose another time"}
      minMinutes={minToday}
    />
  );

  const selectDay = (d: Date) => {
    setDay(d);
    setPicked(null);
    setOwn((o) => ({ ...o, selected: false, open: false, kept: false }));
  };

  const stepPanel = (
    <StepPanel
      stepText={item.text}
      journalTitle={item.journalTitle}
      durationMin={durationMin}
      onDuration={(m) => {
        setDuration(m);
        setPersonSkips(new Set());
      }}
      frequency={frequency}
      onFrequency={changeFrequency}
      frequencyHint={frequencyHint}
      lockFrequency={mode === "reschedule" || mode === "again" ? "once" : mode === "rhythm" ? "rhythm" : null}
      prefer={prefer}
      onPrefer={(p) => {
        setPrefer(p);
        setPicked(null);
        if (isRhythm && !rhythmUserSet) setRhythmTouched(false);
      }}
      freeBusy={freeBusyOn}
    />
  );

  const weekView = (
    <WeekView
      weekStart={weekStart}
      onWeek={setWeekNav}
      minWeekStart={mondayOf(today)}
      busyFor={checkedBusyFor}
      rhythm={rhythm}
      dates={rDates}
      onIfBusy={(v) => patchRhythm({ ifBusy: v })}
    />
  );

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      <PlanTopBar
        title={title}
        connection={connection}
        calendarId={calendarId}
        onCalendarChange={setCalendarId}
        onClose={close}
        locked={mode === "reschedule"}
      />

      <main className="px-4 pb-48 pt-5 sm:px-8 md:pb-28 md:pt-6">
        {isMobile ? (
          <MobileLayout
            item={item}
            frequency={frequency}
            onFrequency={changeFrequency}
            lockFrequency={mode === "reschedule" || mode === "again" ? "once" : mode === "rhythm" ? "rhythm" : null}
            durationMin={durationMin}
            onDuration={setDuration}
            notice={notice}
            once={
              <>
                <MobileDayStrip selected={day} onSelect={selectDay} today={today} dotsFor={dotsFor} />
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDaySheetOpen(true)}
                    className="inline-flex items-center gap-0.5 text-[13.5px] font-medium text-primary"
                  >
                    See my day <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-1">{slotList}</div>
              </>
            }
            rhythm={
              <>
                <RhythmBuilder
                  rhythm={rhythm}
                  onChange={patchRhythm}
                  fit={fit}
                  suggestions={rSuggestions}
                  checking={rhythmChecking}
                  startOptions={startOptions}
                />
                <div className="mt-6 border-t border-foreground/[0.07] pt-6">{weekView}</div>
              </>
            }
          />
        ) : (
          <div className="mx-auto grid max-w-[1376px] items-stretch gap-5 md:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[330px_minmax(0,1.08fr)_minmax(0,1fr)]">
            <div className="md:row-span-2 xl:row-span-1">{stepPanel}</div>
            <PlanCard className="p-6">
              {notice}
              {isRhythm ? (
                <RhythmBuilder
                  rhythm={rhythm}
                  onChange={patchRhythm}
                  fit={fit}
                  suggestions={rSuggestions}
                  checking={rhythmChecking}
                  startOptions={startOptions}
                />
              ) : (
                <>
                  <DayPicker
                    month={month}
                    onMonth={setMonth}
                    selected={day}
                    onSelect={selectDay}
                    today={today}
                    dotsFor={dotsFor}
                    checking={freeBusyOn && monthBusy.isLoading}
                  />
                  <div className="my-5 h-px bg-foreground/[0.07]" />
                  {slotList}
                </>
              )}
            </PlanCard>
            <PlanCard className="flex flex-col p-6">{isRhythm ? weekView : dayColumn}</PlanCard>
          </div>
        )}
      </main>

      {footer}

      {addError === "failed" && (
        <div role="alert" className="fixed inset-x-0 bottom-[150px] z-40 flex justify-center px-4 md:bottom-[88px]">
          <p className="rounded-full bg-[#fbe6e6] px-4 py-2 text-[13px] text-[#8f2a33] shadow-sm">
            Couldn&apos;t reach Google Calendar. Nothing was added — please try again.
          </p>
        </div>
      )}

      <RhythmReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        stepText={item.text}
        rhythmText={rhythmLabel(rhythm)}
        datesText={rhythmSpan}
        calendarText={`${calendarName} · your usual reminders`}
        dates={rDates}
        onToggleSkip={(d) =>
          setPersonSkips((prev) => {
            const next = new Set(prev);
            if (next.has(d)) next.delete(d);
            else next.add(d);
            return next;
          })
        }
        onConfirm={addRhythm}
        confirmLabel={mode === "rhythm" && schedule ? "Update {n} events" : "Add {n} events"}
        pending={pending}
      />

      {isMobile && (
        <Dialog open={daySheetOpen} onOpenChange={setDaySheetOpen}>
          <DialogContent hideClose overlayClassName={calendarOverlayClass} className={calendarDialogClass}>
            <div className="px-5 pb-5 pt-6">
              <DialogTitle className="text-[17px] font-semibold text-foreground">Your day · {formatDay(day)}</DialogTitle>
              <DialogDescription className="mt-0.5 text-[12.5px] text-muted-foreground">
                Busy times only. Tap a free space to place the step.
              </DialogDescription>
              {freeBusyOn ? (
                <DayTimeline busy={dayBusy ?? []} loading={loadingDay} step={timelineStep} onPlace={placeOwn} minMinutes={minToday} compact />
              ) : (
                <p className="mt-4 rounded-2xl bg-foreground/[0.04] px-4 py-3 text-[13.5px] text-muted-foreground">
                  UnfoldWIN can&apos;t see when you&apos;re busy yet, so there&apos;s no day to show.
                </p>
              )}
              <DarkPill size="lg" className="mt-4 w-full" onClick={() => setDaySheetOpen(false)}>
                <Check className="h-4 w-4" />
                {chosen ? `Use ${formatTimeRange(chosen.start, chosen.end)}` : "Done"}
              </DarkPill>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const MOBILE_FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 wks" },
];

/** Phones (M03–M05): one column — the step, how often and how long as chips, then times. */
function MobileLayout({
  item,
  frequency,
  onFrequency,
  lockFrequency,
  durationMin,
  onDuration,
  notice,
  once,
  rhythm,
}: {
  item: MomentumItem;
  frequency: Frequency;
  onFrequency: (f: Frequency) => void;
  lockFrequency: "once" | "rhythm" | null;
  durationMin: number;
  onDuration: (m: number) => void;
  notice: React.ReactNode;
  once: React.ReactNode;
  rhythm: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <div className="rounded-2xl bg-primary/[0.09] px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Next step</p>
        <p className="mt-1 text-[16px] font-medium leading-snug text-foreground">{item.text}</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {MOBILE_FREQUENCIES.map((f) => {
          const locked = lockFrequency === "once" ? f.value !== "once" : lockFrequency === "rhythm" ? f.value === "once" : false;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={frequency === f.value}
              disabled={locked}
              onClick={() => onFrequency(f.value)}
              className={
                frequency === f.value
                  ? "h-8 rounded-full bg-foreground px-3.5 text-[13px] text-white"
                  : "h-8 rounded-full border border-foreground/[0.12] px-3.5 text-[13px] text-foreground disabled:opacity-40"
              }
            >
              {f.label}
            </button>
          );
        })}
      </div>
      <SegmentedControl
        className="mt-3"
        itemClassName="h-8 text-[13px]"
        label="How long, in minutes"
        options={DURATIONS.map((d) => ({ value: d, label: `${d}m` }))}
        value={durationMin}
        onChange={onDuration}
      />
      <div className="mt-5">
        {notice}
        {frequency === "once" ? once : rhythm}
      </div>
    </div>
  );
}

